import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { yesNoOptions } from "src/app/shared/constants/components";
import { QuestionBase } from "../../../shared/components/dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../shared/components/dynamic-form/question-textbox";
import { pattern } from "../../../shared/constants/patterns";
import { UserInfo, UserRights, UserTypes } from "../../../shared/types/user-info";
import { User } from "../users/user.type";

@Injectable()
export class CreateProfileService {
  constructor() {}

  generateQuestions(options: {
    user: User;
    currentUser: UserInfo;
    hidePartner?: boolean;
    hideCompany?: boolean;
    hideIsFutureExpirationNotificationEnabled?: boolean;
  }): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options?.user?.firstName,
        key: "firstName",
        label: "First Name",
        required: true,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "First Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "First Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "First Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options?.user?.lastName,
        key: "lastName",
        label: "Last Name",
        required: true,
        order: 2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Last Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Last Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Last Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options?.user?.email,
        key: "email",
        label: "Email",
        disabled: true,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.email),
            message: "Email is <strong>not valid</strong>",
          },
        ],
        order: 3,
      }),

      new TextboxQuestion({
        value: options?.user?.phone,
        key: "phone",
        label: "Phone",
        order: 4,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Phone is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Phone is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Phone is <strong>too long</strong>",
          },
        ],
      }),

      new DropdownQuestion({
        value: options?.user?.type,
        key: "type",
        label: "User Type",
        order: 5,
        options: this.getUserTypeOptions(),
        disabled: true,
        required: true,
      }),

      new DropdownQuestion({
        value: options?.user?.right,
        key: "right",
        label: "User Right",
        order: 6,
        disabled: true,
        required: true,
        dependsOn: {
          key: "type",
          callback: ({ parentValue: type }) => of(this.getUserRightOptions(options.currentUser, type)),
        },
      }),
      new DropdownQuestion({
        value: options.user?.isFutureExpirationNotificationEnabled,
        key: "isFutureExpirationNotificationEnabled",
        label: "Expiration Email Notifications",
        ignoreInSubmit: !options.currentUser.isPartnerAdminOrManager(),
        hide: options.hideIsFutureExpirationNotificationEnabled || !options.currentUser.isPartnerAdminOrManager(),
        order: 7,
        options: [...yesNoOptions],
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  /**
   * Get the dropdown options to populate on the user type dropdown
   * @returns types list
   */
  private getUserTypeOptions(): DropdownOption[] {
    const options: DropdownOption[] = [
      { label: "Global", value: "global" },
      { label: "Partner", value: "partner" },
      { label: "Company", value: "company" },
    ];

    return options;
  }

  /**
   * Get the dropdown options to populate on the user right dropdown
   * @param currentUser logged user
   * @returns rights list
   */
  private getUserRightOptions(currentUser: UserInfo, type: string): DropdownOption[] {
    let options: DropdownOption[] = [
      { label: "Admin", value: UserRights.Admin },
      { label: "Manager", value: UserRights.Manager },
      { label: "Viewer", value: UserRights.Viewer },
    ];

    if (type === UserTypes.Global && !currentUser.isGlobalAdmin()) {
      options = options.slice(1);
    }

    return options;
  }
}
